import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertCircle,
  Check,
  Edit as EditIcon,
  Loader2,
  Lock,
  Plus,
  Shield,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { ModalPortal } from '../components/ModalPortal';

/**
 * Page /admin/roles — US-ADM-03.
 *
 *  - Liste des rôles du tenant (système + custom).
 *  - Création / édition d'un rôle custom : nom, catégorie, description,
 *    matrice de permissions (modules × actions) à cocher.
 *  - Suppression d'un rôle custom non assigné.
 *  - Les rôles système ne sont pas modifiables ni supprimables (affichage
 *    en lecture seule, badge "Système").
 */

const ROLE_CATEGORIES = [
  'direction',
  'operationnel',
  'technique',
  'qualite',
  'utilisateur',
  'externe',
] as const;
type RoleCategory = (typeof ROLE_CATEGORIES)[number];

const CATEGORY_LABEL: Record<RoleCategory, string> = {
  direction: 'Direction',
  operationnel: 'Opérationnel',
  technique: 'Technique',
  qualite: 'Qualité',
  utilisateur: 'Utilisateur',
  externe: 'Externe',
};

interface RoleRow {
  id: string;
  name: string;
  category: RoleCategory;
  description: string | null;
  is_system: boolean;
  tenant_id: string | null;
  created_at: string;
  _count: { users: number; permissions: number };
}

interface PermissionCatalog {
  id: string;
  module: string;
  action: string;
  code: string;
  description: string | null;
}

interface RoleDetail extends Omit<RoleRow, '_count'> {
  permissions: PermissionCatalog[];
}

const roleFormSchema = z.object({
  name: z.string().min(2, 'Min 2 caractères').max(100),
  category: z.enum(ROLE_CATEGORIES, { message: 'Choisis une catégorie' }),
  description: z.string().max(2000).optional(),
});
type RoleFormInput = z.infer<typeof roleFormSchema>;

export function AdminRolesPage() {
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [permissions, setPermissions] = useState<PermissionCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<RoleRow | 'new' | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, p] = await Promise.all([
          api.get<{ data: RoleRow[] }>('/admin/roles'),
          api.get<{ data: PermissionCatalog[] }>('/admin/permissions'),
        ]);
        setRoles(r.data.data);
        setPermissions(p.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function refreshRoles() {
    try {
      const res = await api.get<{ data: RoleRow[] }>('/admin/roles');
      setRoles(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleDelete(role: RoleRow) {
    if (!confirm(`Supprimer définitivement le rôle "${role.name}" ?`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      refreshRoles();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Shield className="w-7 h-7 mr-2 text-primary-600" />
        Rôles & permissions
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 flex items-center justify-between border-b border-gray-200">
          <p className="text-sm text-gray-600 max-w-xl">
            Les rôles définissent les permissions accordées aux utilisateurs. Les rôles « système »
            sont protégés ; les rôles custom peuvent être créés et personnalisés.
          </p>
          <button
            onClick={() => setEditingRole('new')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau rôle
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nom
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Catégorie
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Utilisateurs
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Permissions
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucun rôle.
                  </td>
                </tr>
              ) : (
                roles.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium flex items-center">
                        {r.name}
                        {r.is_system && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            <Lock className="w-3 h-3 mr-0.5" />
                            Système
                          </span>
                        )}
                      </div>
                      {r.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{r.description}</p>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
                      {CATEGORY_LABEL[r.category] ?? r.category}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className="inline-flex items-center">
                        <Users className="w-3.5 h-3.5 mr-1 text-gray-400" />
                        {r._count.users}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700">
                      {r._count.permissions}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button
                        onClick={() => setEditingRole(r)}
                        title={r.is_system ? 'Voir en lecture seule (rôle système)' : 'Modifier'}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        <EditIcon className="w-3.5 h-3.5 mr-1" />
                        {r.is_system ? 'Voir' : 'Éditer'}
                      </button>
                      {!r.is_system && r._count.users === 0 && (
                        <button
                          onClick={() => handleDelete(r)}
                          title="Supprimer"
                          className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {editingRole && (
        <RoleEditModal
          role={editingRole === 'new' ? null : editingRole}
          permissions={permissions}
          onClose={() => setEditingRole(null)}
          onSaved={() => {
            setEditingRole(null);
            refreshRoles();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal édition / création — meta + matrice permissions
// ─────────────────────────────────────────────────────────────────────────────

function RoleEditModal({
  role,
  permissions,
  onClose,
  onSaved,
}: {
  role: RoleRow | null;
  permissions: PermissionCatalog[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isCreate = role === null;
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const isSystem = role?.is_system ?? false;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RoleFormInput>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: '',
      category: 'utilisateur',
      description: '',
    },
  });

  useEffect(() => {
    if (!role) return;
    (async () => {
      try {
        const res = await api.get<{ data: RoleDetail }>(`/admin/roles/${role.id}`);
        const d = res.data.data;
        reset({
          name: d.name,
          category: d.category,
          description: d.description ?? '',
        });
        setSelectedCodes(new Set(d.permissions.map((p) => p.code)));
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [role, reset]);

  function togglePermission(code: string) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function toggleModule(module: string, hasAll: boolean) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      permissions
        .filter((p) => p.module === module)
        .forEach((p) => {
          if (hasAll) next.delete(p.code);
          else next.add(p.code);
        });
      return next;
    });
  }

  async function onSubmit(values: RoleFormInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        description: values.description || null,
        permission_codes: Array.from(selectedCodes),
      };
      if (role) {
        await api.patch(`/admin/roles/${role.id}`, payload);
      } else {
        await api.post('/admin/roles', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const modules = useMemo(() => {
    const groups = new Map<string, PermissionCatalog[]>();
    for (const p of permissions) {
      if (!groups.has(p.module)) groups.set(p.module, []);
      groups.get(p.module)!.push(p);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-primary-600" />
              {isCreate
                ? 'Nouveau rôle'
                : isSystem
                  ? `Rôle système : ${role.name}`
                  : `Éditer : ${role.name}`}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="role-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Nom" error={errors.name?.message}>
                    <input
                      {...register('name')}
                      disabled={isSystem}
                      className={inputClass(isSystem)}
                    />
                  </Field>
                  <Field label="Catégorie" error={errors.category?.message}>
                    <select
                      {...register('category')}
                      disabled={isSystem}
                      className={inputClass(isSystem)}
                    >
                      {ROLE_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {CATEGORY_LABEL[c]}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Description" error={errors.description?.message}>
                  <textarea
                    rows={2}
                    {...register('description')}
                    disabled={isSystem}
                    className={inputClass(isSystem)}
                    placeholder="Ex. Pilote la maintenance préventive et corrective."
                  />
                </Field>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Permissions ({selectedCodes.size}/{permissions.length})
                  </h3>
                  <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {modules.map(([moduleName, perms]) => {
                      const allChecked = perms.every((p) => selectedCodes.has(p.code));
                      const someChecked = perms.some((p) => selectedCodes.has(p.code));
                      return (
                        <div key={moduleName} className="p-3">
                          <label className="flex items-center cursor-pointer mb-2">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = someChecked && !allChecked;
                              }}
                              onChange={() => !isSystem && toggleModule(moduleName, allChecked)}
                              disabled={isSystem}
                              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm font-semibold text-gray-900 uppercase tracking-wide">
                              {moduleName}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              ({perms.filter((p) => selectedCodes.has(p.code)).length}/
                              {perms.length})
                            </span>
                          </label>
                          <div className="ml-6 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1.5">
                            {perms.map((p) => (
                              <label
                                key={p.code}
                                className={`flex items-center text-sm ${isSystem ? '' : 'cursor-pointer'}`}
                                title={p.description ?? p.code}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedCodes.has(p.code)}
                                  onChange={() => !isSystem && togglePermission(p.code)}
                                  disabled={isSystem}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="ml-1.5 text-gray-700">{p.action}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {isSystem ? 'Fermer' : 'Annuler'}
            </button>
            {!isSystem && (
              <button
                type="submit"
                form="role-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {isCreate ? 'Créer le rôle' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function inputClass(disabled?: boolean) {
  const base =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  return disabled ? `${base} bg-gray-50 cursor-not-allowed` : base;
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
